import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { getClipReadOnlyPlayerConfig } from '@goldcast/player-config/src/utils/clipUtils';
import ClipsListItem from './ClipsListItem';
import { MenuItemsLabelType, menuItems } from '../Sessions/AssetsTable/AssetsTableUtils';
import RowMenuItem from '../Sessions/uiComponents/RowMenuItem';
import useDownloadAsset from '@/hooks/useDownloadAsset';
import useAssetRating from '@/hooks/useAssetRating';
import useClipNavigate from '@/hooks/useClipNavigate';

export default function ClipsListItemWithActions({
  clip,
  isCollapsed,
  onDelete,
  onRenameClip,
  onGenerateSocialPost,
  onClipSelect,
  onDuplicateClip,
  animateClipList = true,
  isInPopover = false,
  hideDuplicateClip = false,
  isMultiSelectMode = false,
  isSelected = false,
  isSelectionLimitReached = false,
  onToggleSelection,
  hideDuration = false
}: {
  clip: Clip;
  isCollapsed: boolean;
  onDelete: (id: string) => void;
  onRenameClip: (clip: Clip) => void;
  onGenerateSocialPost: (clipId: string) => void;
  onClipSelect: (clip: Clip) => void;
  onDuplicateClip: (id: string) => void;
  animateClipList?: boolean;
  isInPopover?: boolean;
  hideDuplicateClip?: boolean;
  isMultiSelectMode?: boolean;
  isSelected?: boolean;
  isSelectionLimitReached?: boolean;
  onToggleSelection?: (clipId: string) => void;
  hideDuration?: boolean;
}) {
  const [searchParams] = useSearchParams();
  const { getAssetRating } = useAssetRating();
  const activeClipId = useMemo(() => searchParams.get('activeClipId'), [searchParams]);
  const isClipActive = activeClipId === clip.id;
  const { feedbackMessage } = getAssetRating(clip.id);
  const { navigateToClip } = useClipNavigate();
  const { onDownloadClip } = useDownloadAsset({ type: 'CLIP', clip }, navigateToClip);

  const playerConfigExists = useMemo(() => {
    return !!getClipReadOnlyPlayerConfig(clip);
  }, [clip]);

  const clipMenuItems = useMemo(() => {
    return menuItems['CLIP'].filter(item => {
      if (item.label === 'Edit') {
        return false;
      }
      if (['Download', 'Rename'].includes(item.label) && !playerConfigExists) {
        return false;
      }

      if (!playerConfigExists && ['Download', 'Rename'].includes(item.label)) {
        return false;
      }

      if (hideDuplicateClip && item.label === 'Duplicate') {
        return false;
      }

      return true;
    });
  }, [feedbackMessage, playerConfigExists, hideDuplicateClip]);

  const onAction = (actionType: MenuItemsLabelType) => {
    switch (actionType) {
      case 'Rename':
        onRenameClip?.(clip);
        break;
      case 'Download':
        onDownloadClip?.();
        break;
      case 'Delete':
        onDelete?.(clip.id);
        break;
      case 'Generate Social Post':
        onGenerateSocialPost?.(clip.asset_metadata.id);
        break;
      case 'Duplicate':
        onDuplicateClip?.(clip.id);
        break;
    }
  };

  return (
    <ClipsListItem
      isInPopover={isInPopover}
      animateClipList={animateClipList}
      clip={clip}
      onClipSelect={onClipSelect}
      isCollapsed={isCollapsed}
      isMultiSelectMode={isMultiSelectMode}
      isSelected={isSelected}
      isSelectionLimitReached={isSelectionLimitReached}
      onToggleSelection={onToggleSelection}
      hideDuration={hideDuration}
    >
      <RowMenuItem content={clip.content} menuItems={clipMenuItems} onAction={onAction} forceShow={isClipActive} />
    </ClipsListItem>
  );
}
